import { Button } from "@/components/ui/button";
import type { PricingTier } from "@/types";

interface CycleToggleProps {
  cycle: "monthly" | "annual";
  onChange: (cycle: "monthly" | "annual") => void;
  tiers: PricingTier[];
}

/**
 * Reads the discount straight off the tier list (PricingTierResource's
 * annualDiscountPercent, sourced from config('billing.annual_discount_percent'))
 * rather than a hardcoded constant, so the label can never drift from what
 * the API actually charges.
 */
function annualDiscountPercent(tiers: PricingTier[]): number {
  return tiers.find((tier) => tier.billingCycle === "annual")?.annualDiscountPercent ?? 0;
}

/**
 * Monthly/annual toggle for the pricing tier pickers (Billing, Subscription
 * Settings, Create Group). Filtering the tier list by cycle is the caller's
 * job; this component only renders the two buttons and reports the choice.
 */
export function CycleToggle({ cycle, onChange, tiers }: CycleToggleProps) {
  const discount = annualDiscountPercent(tiers);
  const annualLabel = discount > 0 ? `Annual (save ${discount}%)` : "Annual";

  return (
    <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/30 p-1">
      <Button
        type="button"
        size="sm"
        variant={cycle === "monthly" ? "default" : "ghost"}
        className="h-8"
        onClick={() => onChange("monthly")}
      >
        Monthly
      </Button>
      <Button
        type="button"
        size="sm"
        variant={cycle === "annual" ? "default" : "ghost"}
        className="h-8"
        onClick={() => onChange("annual")}
      >
        {annualLabel}
      </Button>
    </div>
  );
}
